import { useQuery } from 'react-query';
import { listConversationMessages, type ConversationMessage } from '@/services/conversationsService';

export interface UseConversationMessagesParams {
  conversationId: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
  refetchIntervalMs?: number;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export function useConversationMessages(params: UseConversationMessagesParams) {
  const { conversationId, page = 1, limit = 50, enabled = true, refetchIntervalMs = 5000 } = params;

  return useQuery<PaginatedResponse<ConversationMessage>>({
    queryKey: ['conversation-messages', { conversationId, page, limit }],
    queryFn: async () => {
      const res = await listConversationMessages(conversationId, { page, limit });
      const api = res as unknown as {
        success: boolean;
        data: ConversationMessage[] | PaginatedResponse<ConversationMessage>;
        pagination?: PaginatedResponse<ConversationMessage>['pagination'];
      };

      if (api && (api.data as any)?.data && (api.data as any)?.pagination) {
        return api.data as PaginatedResponse<ConversationMessage>;
      }

      return {
        data: (api.data as ConversationMessage[]) ?? [],
        pagination: api.pagination ?? { page, limit, total: (api.data as ConversationMessage[])?.length ?? 0, pages: 1 },
      } satisfies PaginatedResponse<ConversationMessage>;
    },
    enabled: enabled && Boolean(conversationId),
    refetchInterval: refetchIntervalMs,
    keepPreviousData: true,
  });
}
